import Konva from 'konva';
import type { ComponentDef } from '../../core/library/component-def.js';
import { computeResistorBands } from './resistor-bands.js';
import { addUnifiedValueLabel } from './value-label.js';

/**
 * Renderiza el cuerpo de una resistencia axial cilíndrica.
 * Cápsula con degradado vertical + bandas de color calculadas
 * DINÁMICAMENTE a partir del valor de la instancia (o del valor por
 * defecto de la definición). Si el valor no coincide con la serie E24,
 * no se dibujan bandas. Siempre se pinta encima una etiqueta con el
 * texto del valor.
 */
export function renderResistorBody(
  def: ComponentDef,
  labelText: string,
  rotationDeg: number = 0,
  flipped: boolean = false,
): Konva.Group {
  const g = new Konva.Group();

  const length = def.body.length_mm ?? 7.62;
  const diameter = def.body.diameter_mm ?? 2.54;
  const cornerRadius = def.body.corner_radius_mm ?? diameter / 2;

  const bodyColor = def.appearance.body_color;
  const light = mix(bodyColor, '#ffffff', 0.35);
  const dark = mix(bodyColor, '#000000', 0.25);

  const capsule = new Konva.Rect({
    x: -length / 2,
    y: -diameter / 2,
    width: length,
    height: diameter,
    cornerRadius,
    fillLinearGradientStartPoint: { x: 0, y: -diameter / 2 },
    fillLinearGradientEndPoint: { x: 0, y: diameter / 2 },
    fillLinearGradientColorStops: [
      0, dark,
      0.4, bodyColor,
      0.55, light,
      1, dark,
    ],
    stroke: dark,
    strokeWidth: 0.06,
    listening: true,
  });
  capsule.setAttr('nodeKind', 'body');
  g.add(capsule);

  // Bandas: solo si el valor está en E24. Ignora def.appearance.resistor_bands
  // (obsoleto): las bandas se calculan siempre desde el label/valor.
  const bands = computeResistorBands(labelText);
  if (bands && bands.length > 0) {
    const bandWidth = diameter * 0.35;
    const region = length * 0.6;
    const step = bands.length > 1 ? region / (bands.length - 1) : 0;
    const start = -region / 2;
    for (let i = 0; i < bands.length; i++) {
      const cx = start + step * i;
      const color = bands[i]!;
      const bandLight = mix(color, '#ffffff', 0.3);
      const bandDark = mix(color, '#000000', 0.3);
      const band = new Konva.Rect({
        x: cx - bandWidth / 2,
        y: -diameter / 2,
        width: bandWidth,
        height: diameter,
        fillLinearGradientStartPoint: { x: 0, y: -diameter / 2 },
        fillLinearGradientEndPoint: { x: 0, y: diameter / 2 },
        fillLinearGradientColorStops: [
          0, bandDark,
          0.5, bandLight,
          1, bandDark,
        ],
        listening: false,
      });
      g.add(band);
    }
  }

  // Etiqueta central con el valor. Se envuelve en un grupo con una
  // contra-transformación que la mantiene legible independientemente
  // de la rotación del componente y el flag flipped:
  //   - Cuerpo horizontal (rot 0 / ±180): texto a 0° world.
  //   - Cuerpo vertical (rot ±90):        texto a 90° world.
  //   - Texto nunca aparece invertido aunque el componente esté flipped.
  const target = Math.abs(rotationDeg % 180) === 0 ? 0 : 90;
  const ps = flipped ? -1 : 1;
  const labelGroup = new Konva.Group({
    rotation: ps * (target - rotationDeg),
    scaleY: ps,
    listening: false,
  });

  const fontSize = def.appearance.label?.font_size_mm ?? diameter * 0.55;
  addUnifiedValueLabel(labelGroup, labelText, fontSize, length * 0.95);
  g.add(labelGroup);

  return g;
}

function mix(a: string, b: string, t: number): string {
  const ca = parseHex(a);
  const cb = parseHex(b);
  const r = Math.round(ca.r + (cb.r - ca.r) * t);
  const g = Math.round(ca.g + (cb.g - ca.g) * t);
  const bl = Math.round(ca.b + (cb.b - ca.b) * t);
  return `#${toHex(r)}${toHex(g)}${toHex(bl)}`;
}
function parseHex(hex: string): { r: number; g: number; b: number } {
  let s = hex.replace('#', '');
  if (s.length === 3) s = s.split('').map((c) => c + c).join('');
  return {
    r: parseInt(s.slice(0, 2), 16),
    g: parseInt(s.slice(2, 4), 16),
    b: parseInt(s.slice(4, 6), 16),
  };
}
function toHex(n: number): string {
  return Math.max(0, Math.min(255, n)).toString(16).padStart(2, '0');
}
